<template>
  <div
    class="attention-map-panel"
    :class="{ 'attention-map-panel--stale': degraded }"
  >
    <map-summary
      :summary="summary"
      :age-in-ms="ageInMs"
      :stale="degraded"
    />

    <map-controls
      :classifications="classifications"
      :origins="origins"
      :query="query"
      :matched-count="filteredAssets.length"
      :show-icons="showIcons"
      :show-slice-counts="showSliceCounts"
      @update:classifications="updateUrl({ criticality: $event.join(',') })"
      @update:origins="updateUrl({ origin: $event.join(',') })"
      @update:query="updateUrl({ q: $event })"
      @update:showIcons="updateUrl({ icones: $event ? '' : '0' })"
      @update:showSliceCounts="updateUrl({ contagens: $event ? '' : '0' })"
      @clear="clearFilters"
    />

    <div
      v-if="sessionExpired"
      class="attention-map-panel__notice attention-map-panel__notice--session"
    >
      <h2>{{ $t('attention-map-panel.session-expired') }}</h2>
      <p>{{ $t('attention-map-panel.session-expired-detail') }}</p>
    </div>

    <p v-else-if="degraded" class="attention-map-panel__notice">
      {{ $t('attention-map-panel.stale') }}
    </p>

    <main class="attention-map-panel__body">
      <loader v-if="initialLoading" class="attention-map-panel__loader" size="md" />

      <template v-else>
        <assets-map
          class="attention-map-panel__map"
          :assets="filteredAssets"
          :show-icons="showIcons"
          :show-slice-counts="showSliceCounts"
          @select="openAssetScreen"
          @viewport="onViewportChange"
        />

        <latest-alarms
          class="attention-map-panel__list"
          :alarms="latestAlarms"
          :total="latestTotal"
          :now="now"
          @select="openAlarm"
          @see-all="openScope"
        />
      </template>
    </main>
  </div>
</template>

<script>
import Loader from '@/components/Loading';
import AssetsMap from '@/domain/components/generalPanel/attentionMap/panel/AssetsMap';
import MapControls from '@/domain/components/generalPanel/attentionMap/panel/MapControls';
import MapSummary from '@/domain/components/generalPanel/attentionMap/panel/MapSummary';
import LatestAlarms from '@/domain/components/generalPanel/attentionMap/panel/LatestAlarms';
import loadAllInstallations from '@/domain/services/generalPanel/attentionMapPanel/mapDataLoader';
import {
  buildAssets,
  summarizeAssets,
} from '@/domain/services/generalPanel/attentionMapPanel/attentionMapAggregator';
import applyAttentionMapFilter, {
  filterableClassifications,
  filterableOrigins,
  normalizeValues,
} from '@/domain/services/generalPanel/attentionMapPanel/attentionMapFilter';
import loadLatestAlarms from '@/domain/services/generalPanel/attentionMapPanel/latestAlarms';
import { isStale } from '@/domain/services/generalPanel/communicationStatusPanel/dataAge';
import alarmsReportMixin from '@/domain/mixins/generalPanel/reports/alarmsReportMixin';
import AuthenticationService from '@/services/authentication';

const CLOCK_INTERVAL_IN_MS = 1000;
const MS_IN_SECOND = 1000;

/** Normaliza um parâmetro de rota que pode vir ausente, único ou repetido. */
function toArrayParam(value) {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  return Array.isArray(value) ? value : [value];
}

export default {
  name: 'AttentionMapPanel',
  components: {
    AssetsMap,
    Loader,
    LatestAlarms,
    MapControls,
    MapSummary,
  },
  mixins: [alarmsReportMixin],
  data: () => ({
    assets: [],
    latestAlarms: [],
    latestTotal: 0,
    /** Instalações dentro do recorte visível do mapa; nulo antes do primeiro. */
    viewportInstallations: null,
    lastUpdate: null,
    now: Date.now(),
    initialLoading: true,
    sessionExpired: false,
    refreshTimer: null,
    clockTimer: null,
  }),
  computed: {
    /**
     * Escopo fixado na URL: quem pendura a TV salva o endereço e ele reabre no
     * mesmo recorte. É o único recorte enviado ao servidor.
     */
    scopeFilter() {
      const { query } = this.$route;

      return {
        companiesIds: toArrayParam(query.companiesIds),
        regionalsIds: toArrayParam(query.regionalsIds),
        installationsIds: toArrayParam(query.installationsIds),
      };
    },
    classifications() {
      return normalizeValues(this.$route.query.criticality, filterableClassifications);
    },
    origins() {
      return normalizeValues(this.$route.query.origin, filterableOrigins);
    },
    query() {
      return String(this.$route.query.q || '');
    },
    /**
     * Ícones nos marcadores, ligados por padrão. Fica na URL como o resto: quem
     * pendura a TV salva o endereço com a preferência dentro.
     */
    showIcons() {
      return this.$route.query.icones !== '0';
    },
    /**
     * Contagem de cada fatia do anel do agrupamento, ligada por padrão e na URL
     * como o resto.
     *
     * Opcional porque num mapa de muitas instalações próximas os anéis ficam
     * lado a lado e os números disputam espaço entre si; desligados, o anel volta
     * a ser só a proporção. O **total** do centro não entra nessa opção: sem ele
     * o agrupamento não diz quantos esconde.
     */
    showSliceCounts() {
      return this.$route.query.contagens !== '0';
    },
    /**
     * Placar sempre sobre a frota inteira do escopo, e não sobre o recorte: é a
     * referência contra a qual o recorte é lido.
     */
    summary() {
      return summarizeAssets(this.assets);
    },
    filteredAssets() {
      return applyAttentionMapFilter(this.assets, {
        classifications: this.classifications,
        origins: this.origins,
        query: this.query,
      });
    },
    /**
     * Ativos que a busca por texto deixou de fora, traduzidos em ids para o
     * endpoint dos alarmes — que não sabe procurar por nome.
     *
     * Sem busca ativa vai vazio, e aí o endpoint não filtra por ativo: mandar a
     * frota inteira em `assetsIds` seria um `IN` com centenas de Guids para dizer
     * exatamente nada.
     */
    searchedAssetsIds() {
      if (!this.query.trim()) {
        return [];
      }

      return this.filteredAssets.map((asset) => asset.key);
    },
    /** Tudo o que define o recorte dos Últimos Alarmes, num objeto só. */
    alarmCriteria() {
      return {
        scope: this.scopeFilter,
        installationsIds: this.viewportInstallations,
        assetsIds: this.searchedAssetsIds,
        classifications: this.classifications,
        origins: this.origins,
      };
    },
    /**
     * Assinatura do recorte. O watcher observa a assinatura, e não o objeto: o
     * objeto é recriado a cada ciclo mesmo sem nada mudar, e observá-lo faria a
     * lista chamar a API duas vezes por ciclo — uma pelo recorte, outra pelo
     * relógio.
     */
    alarmCriteriaKey() {
      return JSON.stringify(this.alarmCriteria);
    },
    ageInMs() {
      return this.lastUpdate === null ? null : this.now - this.lastUpdate;
    },
    refreshIntervalInSeconds() {
      return this.$store.getters.autoRefreshInterval;
    },
    degraded() {
      return this.sessionExpired
        || isStale(this.ageInMs, this.refreshIntervalInSeconds);
    },
  },
  watch: {
    alarmCriteriaKey: 'refreshAlarms',
  },
  created() {
    this.startClock();
    this.refresh().then(() => this.startAutoRefresh());
  },
  beforeDestroy() {
    this.stopClock();
    this.stopAutoRefresh();
  },
  methods: {
    /** Filtros vivem na URL para sobreviver a um reload, como o escopo. */
    updateUrl(changes) {
      const query = { ...this.$route.query, ...changes };

      Object.keys(query).forEach((key) => {
        if (query[key] === '' || query[key] === undefined || query[key] === null) {
          delete query[key];
        }
      });

      this.$router.replace({ query }).catch(() => null);
    },
    clearFilters() {
      this.updateUrl({ criticality: '', origin: '', q: '' });
    },
    startClock() {
      this.clockTimer = setInterval(() => {
        this.now = Date.now();
      }, CLOCK_INTERVAL_IN_MS);
    },
    stopClock() {
      if (this.clockTimer) {
        clearInterval(this.clockTimer);
        this.clockTimer = null;
      }
    },
    startAutoRefresh() {
      this.refreshTimer = setInterval(
        () => this.refresh(),
        this.refreshIntervalInSeconds * MS_IN_SECOND,
      );
    },
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
    /**
     * Um ciclo de atualização. O conjunto exibido só é substituído quando todas
     * as páginas chegam: falhou qualquer uma, a tela mantém o último conjunto
     * completo e deixa o relógio de idade envelhecer.
     */
    async refresh() {
      if (this.sessionExpired) {
        this.resumeSession();
        return;
      }

      try {
        const installations = await loadAllInstallations(this.scopeFilter);
        this.assets = buildAssets(installations);
        this.lastUpdate = Date.now();
        this.now = this.lastUpdate;
        this.refreshAlarms();
      } catch (error) {
        if (!this.$store.getters.authenticated) {
          this.sessionExpired = true;
        }
      } finally {
        this.initialLoading = false;
      }
    },
    /**
     * Com a sessão encerrada, o ciclo continua mas não chama a API: só confere se
     * alguém entrou no sistema neste navegador. Mesmo tratamento do Painel de
     * Status de Comunicação. Ver
     * docs/adr/0004-painel-permanece-exibido-quando-a-sessao-expira.md
     */
    resumeSession() {
      if (AuthenticationService.hasStoredSession()) {
        this.reloadPage();
      }
    },
    reloadPage() {
      window.location.reload();
    },
    /**
     * Saída pelo botão do Popup: a tela do Ativo, em outra aba.
     *
     * Os dois gestos da tela levam a lugares diferentes de propósito. O botão diz
     * "analisar ativo" — o assunto é o equipamento, e é na tela do Ativo que se
     * olha equipamento. A linha da lista fala de um alarme, e vai para Alarmes e
     * Avisos, onde se age sobre ele.
     *
     * Vai pela rota nomeada, e não por caminho montado à mão: com string, uma
     * mudança de path passaria a existir em dois lugares e um deles ficaria para
     * trás. Mesmo destino do "Acessar" da lista de ativos.
     */
    openAssetScreen(asset) {
      const routeData = this.$router.resolve({
        name: 'asset.asset-id',
        params: {
          installationId: asset.installationId,
          assetId: asset.key,
        },
      });

      window.open(routeData.href, '_blank');
    },
    /**
     * O mapa mudou de recorte. Guardar basta: o watcher da assinatura refaz a
     * chamada, e concentrar a decisão num lugar só evita que zoom e ciclo
     * disparem duas chamadas para o mesmo estado.
     */
    onViewportChange(installationsIds) {
      this.viewportInstallations = installationsIds;
    },
    /**
     * Recarrega os Últimos Alarmes e Avisos do recorte.
     *
     * O contador de requisições existe porque o recorte muda mais rápido que a
     * rede responde: arrastar o mapa duas vezes dispara duas chamadas, e sem o
     * contador a primeira, se chegasse atrasada, sobrescreveria a segunda — a
     * lista passaria a mostrar um recorte que ninguém está mais olhando.
     */
    refreshAlarms() {
      this.alarmRequestId = (this.alarmRequestId || 0) + 1;
      const requestId = this.alarmRequestId;

      return loadLatestAlarms(this.alarmCriteria)
        .then(({ items, total }) => {
          if (requestId !== this.alarmRequestId) {
            return;
          }

          this.latestAlarms = items;
          this.latestTotal = total;
        })
        .catch(() => {
          // Falha de ciclo mantém a última lista completa, como o mapa: meia
          // lista apresentada como inteira é pior que uma lista envelhecida.
        });
    },
    /**
     * Saída pela lista: Alarmes e Avisos filtrado naquele Ativo, em outra aba. A
     * aba nova é o que preserva o painel — numa TV, navegar para fora encerraria a
     * vigilância sem ninguém por perto para trazê-la de volta.
     */
    openAlarm(alarm) {
      this.openAlarmAndWarning({
        companiesIds: [alarm.companyId],
        installationsIds: [alarm.installationId],
        assetsIds: [alarm.assetId],
      });
    },
    openScope() {
      this.openAlarmAndWarning({
        companiesIds: this.scopeFilter.companiesIds || [],
        regionalIds: this.scopeFilter.regionalsIds || [],
        installationsIds: this.scopeFilter.installationsIds || [],
        classifications: this.classifications,
      });
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/components/communication-status-panel-fluid"
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

// Os avisos medem a página: numa TV de parede, um alerta de sessão expirada em
// corpo de notebook não é lido de longe — e ele é justamente o que precisa ser.
.attention-map-panel
  background: var(--panel-background)
  container-type: inline-size
  display: flex
  flex-direction: column
  // O rodapé da aplicação fica fora deste componente.
  height: calc(100vh - #{$footer-height})

  &__notice
    background: var(--panel-stale-notice)
    color: var(--panel-stale)
    +panel-fluid(font-size, 1rem, 1.25cqw, 2.2rem)
    font-weight: 700
    margin: 0
    padding: 0.5rem 1.25rem
    text-align: center

    &--session
      padding: 1rem 1.25rem

      h2
        +panel-fluid(font-size, 1.1rem, 1.4cqw, 2.8rem)
        margin: 0 0 0.25rem

      p
        +panel-fluid(font-size, 0.85rem, 1.06cqw, 2rem)
        font-weight: 400
        margin: 0

  &__body
    display: flex
    flex: 1 1 auto
    min-height: 0

  &__loader
    margin: auto

  &__map
    flex: 1 1 auto
    min-width: 0

  // A moldura da lista é dela: aqui vai só a largura. Proporção, e não medida
  // fixa: numa TV de parede o mapa engolia a tela e a lista virava uma tira. O
  // piso de 22rem é onde "TR-02 · SE Belo Horizonte" ainda cabe sem cortar; o
  // teto impede que a lista tome a tela num monitor ultralargo. O mapa nunca
  // fica com menos de 70%.
  &__list
    flex: 0 0 clamp(22rem, 30%, 60rem)
    max-width: 100%

  // Telas estreitas empilham: o mapa perde largura útil antes da lista perder
  // sentido.
  +media-max-md()
    &__body
      flex-direction: column

    &__map
      min-height: 22rem

    // Empilhado, a faixa que separava lateralmente passa para cima.
    &__list
      border-left: none
      border-top: 1px solid var(--panel-border)
      flex: 1 1 auto
</style>
